import React, { useState, useMemo } from 'react';
import { FiPlus, FiEdit, FiTrash2, FiBarChart2 } from 'react-icons/fi';
import Swal from 'sweetalert2';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import { AddInterest, EditInterest } from '../components/interests';
import { useInterests, useDeleteInterest } from '../hooks/useInterests';

const Interests = ({ user }) => {
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedInterest, setSelectedInterest] = useState(null);

  // Fetch interests using React Query
  const {
    data: interestsResponse,
    isLoading,
    error,
    isFetching,
  } = useInterests(pagination.current, pagination.pageSize);
  console.log(interestsResponse);

  // Mutations
  const deleteInterestMutation = useDeleteInterest();

  // Memoized interests data
  const { interestsData, totalRecords } = useMemo(() => {
    if (!interestsResponse) {
      return { interestsData: [], totalRecords: 0 };
    }

    // Ensure we always return an array for interestsData
    const interests = interestsResponse.interests.data || interestsResponse.data || [];
    const total = interestsResponse.interests.total || interestsResponse.count || 0;

    return {
      interestsData: Array.isArray(interests) ? interests : [],
      totalRecords: total
    };
  }, [interestsResponse]);

  // Check if delete mutation is loading
  const isMutating = deleteInterestMutation.isPending;

  // Get error message from mutations or query
  const errorMessage = error?.message || 
                      deleteInterestMutation.error?.message;

  // Table columns configuration
  const columns = [
    {
      key: 'id',
      title: 'ID',
      sortable: true,
    },
    {
      key: 'interest',
      title: 'Interest (EN)',
      sortable: true,
      render: (value, row) => (
        <span className="text-gray-900 font-medium">
          {value?.en || 'N/A'}
        </span>
      ),
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (_, row) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleEditInterest(row)}
            className="text-green-600 hover:text-green-800 p-1"
            title="Edit Interest"
            disabled={isMutating}
          >
            <FiEdit className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleDeleteInterest(row)}
            className="text-red-600 hover:text-red-800 p-1"
            title="Delete Interest"
            disabled={isMutating}
          >
            <FiTrash2 className="h-4 w-4" />
          </button>
        </div>
      )
    }
  ];

  // Breadcrumbs
  const breadcrumbs = [
    { label: 'Dashboard', href: '/dashboard' },
    { label: 'Interests', href: '/dashboard/interests' },
  ];

  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, current: page }));
  };

  const handlePageSizeChange = (pageSize, current) => {
    setPagination(prev => ({ ...prev, pageSize, current }));
  };

  const handleAddInterest = () => {
    setSelectedInterest(null);
    setShowAddModal(true);
  };

  const handleEditInterest = (interest) => {
    setSelectedInterest(interest);
    setShowEditModal(true);
  };

  const handleDeleteInterest = async (interest) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete interest "${interest.interest?.en || 'N/A'}". This action cannot be undone!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      background: '#ffffff',
      color: '#333',
      customClass: {
        popup: 'rounded-lg shadow-xl',
        confirmButton: 'bg-[#dc2626] text-white font-bold py-2 px-4 rounded',
        cancelButton: 'bg-[#6b7280] text-white font-bold py-2 px-4 rounded',
      }
    });

    if (result.isConfirmed) {
      try {
        await deleteInterestMutation.mutateAsync(interest.id);
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Interest has been deleted successfully.',
          timer: 2000,
          showConfirmButton: false,
          background: '#ffffff',
          color: '#333',
          customClass: {
            popup: 'rounded-lg shadow-xl',
          }
        });
      } catch (error) {
        console.error('Error deleting interest:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to delete interest. Please try again.',
          confirmButtonText: 'OK',
          background: '#ffffff',
          color: '#333',
          customClass: {
            popup: 'rounded-lg shadow-xl',
          }
        });
      }
    }
  };

  return (
    <Layout>
      <div className="">
        <PageHeader
          title="Interests Management"
          breadcrumbs={breadcrumbs}
          action={{
            label: 'Add Interest',
            onClick: handleAddInterest,
            icon: <FiPlus className="h-4 w-4" />,
          }}
        />

        {/* Error Message */}
        {errorMessage && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm">{errorMessage}</p>
          </div>
        )}

        
        {/* Add Client Button */}
        <div className="flex justify-end !mb-4">
          <button
            onClick={handleAddInterest}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50"
            disabled={isMutating}
          >
            <FiPlus className="h-4 w-4 mr-2" />
            Add Interest
          </button>
        </div>

        {/* Interests Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <DataTable
            data={interestsData}
            columns={columns}
            filterable={false}
            loading={isLoading || isFetching}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: totalRecords,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => 
                `${range[0]}-${range[1]} of ${total} interests`,
            }}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            emptyText="No interests found"
            emptyIcon={<FiBarChart2 className="h-12 w-12 text-gray-400" />}
          />
        </div>

        {/* Add Interest Modal */}
        {showAddModal && (
          <AddInterest
            isOpen={showAddModal}
            onClose={() => setShowAddModal(false)}
          />
        )}

        {/* Edit Interest Modal */}
        {showEditModal && (
          <EditInterest
            isOpen={showEditModal}
            onClose={() => setShowEditModal(false)}
            interest={selectedInterest}
          />
        )}
      </div>
    </Layout>
  );
};

export default Interests; 